import { motion } from "framer-motion";
import React, { useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { FieldsHorizontal } from "@/components/FieldsHorizontal";
import { FanComments } from "@/components/FanComments";
import { Instagram, Mail, Film, Users, Sparkles, TrendingUp } from "lucide-react";
import { SiX, SiLinkedin } from "react-icons/si";
import { FilmGrainOverlay } from "@/components/FilmGrainOverlay";
import { SocialLinks } from "@/components/SocialLinks";
import { IMAGES } from "@/assets/images";
import { CREATIVE_PARTNERS } from "@/lib/index";
import { springPresets, sectionContainer, sectionItemUp, sectionItemLeft, sectionItemRight, sectionItemScale } from "@/lib/motion";
import { ParticleField } from "@/components/ParticleField";
import { ScrollParticles } from "@/components/ScrollParticles";
import { CursorGlow } from "@/components/CursorGlow";
import { NavDots } from "@/components/NavDots";
import { SectionTransitionLabel } from "@/components/SectionTransitionLabel";
import { AmbientSound } from "@/components/AmbientSound";
import { LoadingScreen } from "@/components/LoadingScreen";
import { ProgressBar } from "@/components/ProgressBar";
import { Typewriter } from "@/components/Typewriter";
import { SectionFlash } from "@/components/SectionFlash";

/** Fields背景スライドショー（ズームイン＋クロスフェード） */
const FIELDS_IMAGES = [
  IMAGES.FIELDS_BG,
  IMAGES.FIELDS_BG2,
  IMAGES.FIELDS_BG3,
  IMAGES.FIELDS_BG4,
];

function FieldsBgSlideshow() {
  const [current, setCurrent] = useState(0);
  const [prev, setPrev] = useState<number | null>(null);
  const [fading, setFading] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => {
      setPrev(current);
      setFading(true);
      setCurrent((c) => (c + 1) % FIELDS_IMAGES.length);
      setTimeout(() => {
        setPrev(null);
        setFading(false);
      }, 1200);
    }, 4000);
    return () => clearInterval(timer);
  }, [current]);

  return (
    <div className="absolute inset-0 z-0 overflow-hidden">
      {/* 前の画像（フェードアウト） */}
      {prev !== null && (
        <img
          key={`prev-${prev}`}
          src={FIELDS_IMAGES[prev]}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
          style={{
            opacity: fading ? 0 : 0.62,
            transition: "opacity 1.2s ease",
            transform: "scale(1.06) translateZ(0)",
          }}
        />
      )}
      {/* 現在の画像（フェードイン＋ズームイン） */}
      <img
        key={`curr-${current}`}
        src={FIELDS_IMAGES[current]}
        alt=""
        className="absolute inset-0 w-full h-full object-cover"
        style={{
          opacity: 0.62,
          animation: "fieldsZoomIn 4.8s ease-out forwards",
          transform: "translateZ(0)",
        }}
      />
    </div>
  );
}

/** Links背景スライドショー（ズームイン＋クロスフェード） */
const LINKS_IMAGES = [
  IMAGES.CAT_ALLEY,
  IMAGES.LINKS_BG2,
  IMAGES.LINKS_BG3,
];

function LinksBgSlideshow() {
  const [current, setCurrent] = useState(0);
  const [prev, setPrev] = useState<number | null>(null);
  const [fading, setFading] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => {
      setPrev(current);
      setFading(true);
      setCurrent((c) => (c + 1) % LINKS_IMAGES.length);
      setTimeout(() => {
        setPrev(null);
        setFading(false);
      }, 1200);
    }, 4000);
    return () => clearInterval(timer);
  }, [current]);

  return (
    <div className="absolute inset-0 z-0 overflow-hidden">
      {prev !== null && (
        <img
          key={`prev-${prev}`}
          src={LINKS_IMAGES[prev]}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
          style={{
            opacity: fading ? 0 : 0.55,
            transition: "opacity 1.2s ease",
            objectPosition: "center 20%",
            transform: "scale(1.06) translateZ(0)",
          }}
        />
      )}
      <img
        key={`curr-${current}`}
        src={LINKS_IMAGES[current]}
        alt=""
        className="absolute inset-0 w-full h-full object-cover"
        style={{
          opacity: 0.55,
          objectPosition: "center 20%",
          animation: "fieldsZoomIn 4.8s ease-out forwards",
          transform: "translateZ(0)",
        }}
      />
    </div>
  );
}

/** 画面外でも事前に動画を読み込むフック（rootMargin で先読み距離を制御） */
// ※ IntersectionObserver の rootMargin は px または % のみ有効（vh 不可）
function useLazyVideo(src: string, rootMargin = "900px") {
  const ref = useRef<HTMLElement | null>(null);
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setReady(true); obs.disconnect(); } },
      { rootMargin }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return { ref, src: ready ? src : undefined };
}

/* ─── ContactForm ─────────────────────────────────────────── */
type FormState = 'idle' | 'sending' | 'success' | 'error';

function ContactForm() {
  const [name,    setName]    = useState('');
  const [email,   setEmail]   = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [status,  setStatus]  = useState<FormState>('idle');
  const [errMsg,  setErrMsg]  = useState('');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !message.trim()) return;
    setStatus('sending');
    setErrMsg('');
    try {
      const { data, error } = await supabase.functions.invoke('send-contact-mail', {
        body: { name, email, subject, message, form_type: 'contact' },
      });
      if (error) throw error;
      if (data?.success) {
        setStatus('success');
        setName(''); setEmail(''); setSubject(''); setMessage('');
      } else {
        throw new Error(data?.error || 'Unknown error');
      }
    } catch (err) {
      setStatus('error');
      setErrMsg(err instanceof Error ? err.message : String(err));
    }
  }

  return (
    <div>
      <div className="text-center mb-8">
        <Mail className="w-12 h-12 text-primary mx-auto mb-6" />
        <h2 className="text-2xl md:text-3xl font-bold mb-1">Contact</h2>
        <p className="text-sm text-muted-foreground/60 mb-4 tracking-widest">お問い合わせ</p>
        <p className="text-sm sm:text-base text-foreground/90 mb-1 leading-relaxed">
          Open to projects, collaborations, and creative partnerships.
        </p>
        <p className="text-xs sm:text-sm text-muted-foreground/60 leading-relaxed">
          制作依頼・コラボのご相談はこちらから。
        </p>
      </div>

      {status === 'success' ? (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center py-8"
        >
          <div className="text-4xl mb-4">✉️</div>
          <p className="text-lg font-semibold text-primary mb-2">送信しました</p>
          <p className="text-sm text-muted-foreground">Message sent. Thank you!</p>
          <button
            onClick={() => setStatus('idle')}
            className="mt-6 text-xs text-muted-foreground/60 hover:text-muted-foreground underline transition-colors"
          >
            もう一通送る
          </button>
        </motion.div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-muted-foreground mb-1 tracking-wider">お名前 <span className="text-red-400">*</span></label>
              <input
                type="text" value={name} onChange={e => setName(e.target.value)}
                required placeholder="Tachiki Keinosuke"
                className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/30 focus:outline-none focus:border-primary/50 focus:bg-white/8 transition-all"
              />
            </div>
            <div>
              <label className="block text-xs text-muted-foreground mb-1 tracking-wider">メールアドレス <span className="text-red-400">*</span></label>
              <input
                type="email" value={email} onChange={e => setEmail(e.target.value)}
                required placeholder="your@email.com"
                className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/30 focus:outline-none focus:border-primary/50 focus:bg-white/8 transition-all"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs text-muted-foreground mb-1 tracking-wider">件名</label>
            <input
              type="text" value={subject} onChange={e => setSubject(e.target.value)}
              placeholder="Project inquiry / ご相談内容"
              className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/30 focus:outline-none focus:border-primary/50 focus:bg-white/8 transition-all"
            />
          </div>
          <div>
            <label className="block text-xs text-muted-foreground mb-1 tracking-wider">メッセージ <span className="text-red-400">*</span></label>
            <textarea
              value={message} onChange={e => setMessage(e.target.value)}
              required rows={5} placeholder="Tell me about your project..."
              className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/30 focus:outline-none focus:border-primary/50 focus:bg-white/8 transition-all resize-none"
            />
          </div>
          {status === 'error' && (
            <p className="text-xs text-red-400/80">{errMsg || '送信に失敗しました。しばらく後にお試しください。'}</p>
          )}
          <div className="flex items-center justify-between pt-2">
            <p className="text-[10px] text-muted-foreground/40">
              <span className="text-red-400">*</span> 必須項目
            </p>
            <button
              type="submit"
              disabled={status === 'sending'}
              className="inline-flex items-center gap-2 px-8 py-3 bg-primary text-primary-foreground rounded-xl font-semibold hover:bg-primary/90 transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 text-sm"
            >
              <Mail className="w-4 h-4" />
              {status === 'sending' ? '送信中...' : 'Send Message'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

export default function Home() {
  // 画面1枚以上手前から読み込み開始（px 指定 — vh は使用不可）
  const aboutVideo   = useLazyVideo("/video/about.mp4",   "1000px");
  const conceptVideo = useLazyVideo("/video/links.mp4",   "1000px");
  const techVideo    = useLazyVideo("/video/tech.mp4",    "900px");
  const contactVideo = useLazyVideo("/video/contact.mp4", "900px");

  return (
    <div className="min-h-screen bg-background text-foreground overflow-x-hidden">
      <ProgressBar />
      <LoadingScreen />
      <CursorGlow />
      <ParticleField />
      <ScrollParticles />
      <NavDots />
      <SectionTransitionLabel />
      <AmbientSound />
      <FilmGrainOverlay />
      {/* C: セクション切り替えフラッシュ — 削除するには下の1行と上のimportを消すだけ */}
      <SectionFlash />

      {/* Section 1: Hero */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* 背景: 動画 + カラー背景のみ */}
        <div className="absolute inset-0 z-0">
          {/* 動画背景 — GPU昇格でスマホスクロールズレ対策 */}
          <video
            autoPlay
            loop
            muted
            playsInline
            className="absolute inset-0 w-full h-full object-cover opacity-55"
            style={{
              transform: "translateZ(0)",
              willChange: "transform",
              backfaceVisibility: "hidden",
            }}
          >
            <source src="/video/hero.mp4" type="video/mp4" />
          </video>
          {/* カラーオーバーレイ */}
          <div className="absolute inset-0 bg-gradient-to-br from-purple-900/30 via-transparent to-blue-900/30"
            style={{ transform: "translateZ(0)", willChange: "transform" }} />
          {/* B: 呼吸アニメーション — 暗さグラデーションがゆっくり波打つ */}
          <motion.div
            className="absolute inset-0 bg-gradient-to-b from-background/60 via-background/30 to-background/80"
            animate={{ opacity: [1.4, 0.72, 1.4] }}
            transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
            style={{ transform: "translateZ(0)" }}
          />
        </div>

        {/* 装飾的なグロー */}
        <div className="absolute inset-0 z-0">
          <motion.div
            animate={{
              scale: [1, 1.2, 1],
              opacity: [0.3, 0.5, 0.3]
            }}
            transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
            className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/20 rounded-full blur-3xl"
          />
          <motion.div
            animate={{
              scale: [1, 1.3, 1],
              opacity: [0.3, 0.5, 0.3]
            }}
            transition={{ duration: 10, repeat: Infinity, ease: "easeInOut", delay: 1 }}
            className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-accent/20 rounded-full blur-3xl"
          />
        </div>

        <motion.div
          className="relative z-10 container mx-auto px-4 text-center max-w-5xl"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={springPresets.gentle}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ ...springPresets.gentle, delay: 0.2 }}
            className="mb-8"
          >
            <h1
              className="text-3xl sm:text-5xl md:text-7xl lg:text-8xl font-light mb-4 tracking-widest title-hue-cycle"
              style={{
                fontFamily: "'Rajdhani', sans-serif",
                isolation: "isolate",
              }}
            >
              PictWork STUDIO
            </h1>
            <p
              className="text-sm sm:text-lg md:text-xl mb-2 tracking-wider"
              style={{
                color: "oklch(0.72 0.14 195)",
                textShadow: "0 0 12px oklch(0.65 0.18 195 / 0.6), 0 0 30px oklch(0.55 0.15 195 / 0.3)"
              }}
            >
              Visual Artist / AI Filmmaker
            </p>
            <p className="text-xs sm:text-base md:text-lg text-muted-foreground/80">
              Keinosuke Tachiki
            </p>
          </motion.div>

          <motion.p
            className="text-base sm:text-xl md:text-3xl font-light text-foreground/90 mb-12 leading-relaxed min-h-[4rem] sm:min-h-[5rem]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ ...springPresets.gentle, delay: 0.4 }}
          >
            <Typewriter
              startDelay={900}
              charDelay={48}
              lineDelay={400}
              lines={[
                {
                  text: "Stories beyond reality",
                  className: "block",
                },
                {
                  text: "現実と交差する異世界の日常を描く",
                  className: "text-sm sm:text-lg md:text-xl text-muted-foreground mt-2 block",
                },
              ]}
            />
          </motion.p>

          {/* Creative Partners — 1枚のコンパクトカードにまとめ */}
          <motion.div
            className="flex justify-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ...springPresets.gentle, delay: 0.6 }}
          >
            <div
              className="relative rounded-xl sm:rounded-2xl px-3 py-2 sm:px-5 sm:py-3"
              style={{
                background: "transparent",
                border: "1px solid rgba(255,255,255,0.18)",
                boxShadow: "0 0 12px rgba(0,180,200,0.08), 0 0 1px rgba(255,255,255,0.1)"
              }}
            >
              {/* ラベル */}
              <p className="text-[8px] sm:text-[10px] tracking-[0.15em] sm:tracking-[0.2em] text-white/30 uppercase text-center mb-1.5 sm:mb-2.5">
                Creative Partners
              </p>
              {/* パートナー横並び */}
              <div className="flex items-center gap-2.5 sm:gap-6">
                {CREATIVE_PARTNERS.map((partner, i) => (
                  <div key={partner.name} className="flex items-center gap-1.5 sm:gap-2">
                    {i > 0 && (
                      <span className="w-px h-4 sm:h-5 bg-white/10" />
                    )}
                    {i > 0 && (
                      <span className="hidden" />
                    )}
                    <div className="text-center">
                      <p className="text-[10px] sm:text-sm font-semibold text-white/85 leading-none mb-0.5">{partner.name}</p>
                      <p className="text-[8px] sm:text-[10px] text-white/35 leading-none tracking-wide">{partner.role}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>

          {/* ===== ファン特設サイトリンク BLOCK START（削除する場合はここからBLOCK ENDまで丸ごと削除） ===== */}
          <motion.div
            className="flex justify-center mb-6"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ...springPresets.gentle, delay: 0.85 }}
          >
            <a
              href="#/mato-city-gov"
              className="group flex items-center gap-2 px-4 py-2 rounded-lg border border-red-500/35 bg-black/20 hover:bg-red-900/25 hover:border-red-400/65 transition-all duration-500"
              style={{ boxShadow: "0 0 16px rgba(200,30,30,0.10)" }}
            >
              <span className="text-red-400/90 text-lg animate-pulse">⚠</span>
              <div className="text-left">
                <div className="text-[10px] font-black tracking-widest text-red-400/85 uppercase group-hover:text-red-300 transition-colors leading-tight">
                  ファン特設 獣人警戒サイト
                </div>
                <div className="text-[9px] text-white/35 group-hover:text-white/60 transition-colors tracking-wider">
                  Demon City — Beastman Hazard Alert
                </div>
              </div>
              <span className="text-red-400/30 group-hover:text-red-300/70 transition-colors text-xs ml-1">›</span>
            </a>
          </motion.div>
          {/* ===== ファン特設サイトリンク BLOCK END ===== */}

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ ...springPresets.gentle, delay: 1 }}
          >
            <a
              href="#about"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary/10 hover:bg-primary/20 border border-primary/30 rounded-full transition-all duration-300"
            >
              <span className="text-sm font-medium">Learn More</span>
              <motion.div
                animate={{ y: [0, 4, 0] }}
                transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                </svg>
              </motion.div>
            </a>
          </motion.div>
        </motion.div>
      </section>

      {/* Section 2: About */}
      <section id="about" ref={aboutVideo.ref as React.RefObject<HTMLElement>} className="relative min-h-screen flex flex-col items-center overflow-hidden py-12 px-4 bg-background">
        <div className="absolute inset-0 z-0">
          {/* 動画背景 — Lazy Load + GPU昇格 */}
          <video
            autoPlay
            loop
            muted
            playsInline
            className="absolute inset-0 w-full h-full object-cover opacity-50"
            style={{ transform: "translateZ(0)", willChange: "transform", backfaceVisibility: "hidden" }}
          >
            {aboutVideo.src && <source src={aboutVideo.src} type="video/mp4" />}
          </video>
          <div className="absolute inset-0 bg-gradient-to-b from-background/70 via-background/40 to-background/80" />
        </div>

        <motion.div
          className="relative z-10 container mx-auto max-w-6xl w-full"
          variants={sectionContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
        >

          {/* ── ヘッダー行 ── */}
          <motion.div variants={sectionItemUp} className="text-center mb-6">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-1">About</h2>
            <p className="text-xs sm:text-sm text-muted-foreground">AI Visual Director & IP Story Architect</p>
          </motion.div>

          {/* ── 2カラムレイアウト ── */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:items-stretch">

            {/* 左カード */}
            <motion.div
              variants={sectionItemLeft}
              className="h-full"
            >
              <div className="bg-black/50 border border-white/8 rounded-xl p-5 sm:p-6 h-full flex flex-col justify-between gap-5">
                {/* プロフィール */}
                <div>
                  <p className="text-sm sm:text-base text-white/95 leading-relaxed mb-3">
                    Exploring the future of <span className="text-cyan-300 font-semibold">character-driven storytelling</span> through generative AI tools.
                  </p>
                  <p className="text-xs sm:text-sm text-white/70 leading-relaxed">
                    As a professional photographer and film director, I integrate cinematic, dramatic, anime-inspired, and subcultural expressions into AI-generated video — creating a new hybrid format: <span className="text-cyan-300 font-semibold">"Movie Comics"</span>.
                  </p>
                </div>
                {/* 区切り線 */}
                <div className="border-t border-white/10" />
                {/* Vision */}
                <div className="flex items-start gap-3">
                  <div className="inline-flex items-center justify-center w-8 h-8 bg-cyan-500/10 rounded-lg text-cyan-300 flex-shrink-0 mt-0.5">
                    <Sparkles className="w-4 h-4" />
                  </div>
                  <div>
                    <h3 className="text-sm sm:text-base font-bold mb-1 text-white/95">Vision</h3>
                    <p className="text-xs sm:text-sm text-white/70 leading-relaxed">
                      The next massive creative platform after WEBTOON-style vertical comics will be <span className="text-cyan-300 font-semibold">serialized AI-native moving narratives</span> — where AI companies and IP-driven platforms become the new "publishers" of this era.
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* 右カード: 実績3つ + Collaborations を1枚にまとめる */}
            <motion.div
              variants={sectionItemRight}
              className="h-full"
            >
              <div className="bg-black/50 border border-white/8 rounded-xl p-5 sm:p-6 h-full flex flex-col justify-between gap-5">
                {/* 実績3つ横並び */}
                <div className="grid grid-cols-3 gap-2">
                <div className="bg-black/40 border border-white/8 rounded-lg p-3 text-center">
                    <div className="inline-flex items-center justify-center w-8 h-8 bg-cyan-500/10 rounded-lg text-cyan-300 mb-2">
                      <Users className="w-4 h-4" />
                    </div>
                    <h3 className="text-lg sm:text-2xl font-bold text-cyan-300 leading-none mb-1">23K+</h3>
                    <p className="text-[10px] sm:text-xs text-white/70 leading-tight">Instagram<br/>Followers</p>
                  </div>
                  <div className="bg-black/40 border border-white/8 rounded-lg p-3 text-center">
                    <div className="inline-flex items-center justify-center w-8 h-8 bg-cyan-500/10 rounded-lg text-cyan-300 mb-2">
                      <Film className="w-4 h-4" />
                    </div>
                    <h3 className="text-lg sm:text-2xl font-bold text-cyan-300 leading-none mb-1">300+</h3>
                    <p className="text-[10px] sm:text-xs text-white/70 leading-tight">Serialized<br/>Episodes</p>
                  </div>
                  <div className="bg-black/40 border border-white/8 rounded-lg p-3 text-center">
                    <div className="inline-flex items-center justify-center w-8 h-8 bg-cyan-500/10 rounded-lg text-cyan-300 mb-2">
                      <TrendingUp className="w-4 h-4" />
                    </div>
                    <h3 className="text-lg sm:text-2xl font-bold text-cyan-300 leading-none mb-1">1yr+</h3>
                    <p className="text-[10px] sm:text-xs text-white/70 leading-tight">Continuous<br/>Publishing</p>
                  </div>
                </div>
                {/* 区切り線 */}
                <div className="border-t border-white/10" />
                {/* Collaborations */}
                <div>
                  <h3 className="text-sm sm:text-base font-bold mb-3 text-center text-white/95">Selected Collaborations</h3>
                  <div className="flex flex-wrap justify-center gap-2">
                    <div className="px-3 py-1.5 bg-cyan-500/10 border border-cyan-500/20 rounded-full">
                      <span className="text-xs font-semibold text-cyan-300">Kling Official</span>
                    </div>
                    <div className="px-3 py-1.5 bg-cyan-500/10 border border-cyan-500/20 rounded-full">
                      <span className="text-xs font-semibold text-cyan-300">Tripo Official</span>
                    </div>
                    <div className="px-3 py-1.5 bg-cyan-500/10 border border-cyan-500/20 rounded-full">
                      <span className="text-xs font-semibold text-cyan-300">International Client Work</span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

          </div>{/* /grid */}
        </motion.div>

        {/* Fan Comments popup area */}
        <div
          style={{
            position: "relative",
            width: "100%",
            alignSelf: "stretch",
            height: "clamp(360px, 50vh, 600px)",
            overflow: "hidden",
            marginTop: "clamp(24px, 4vh, 48px)",
            flexShrink: 0,
          }}
        >
          <FanComments />
        </div>
      </section>

      {/* Section 3: Fields — 背景・sectionはFieldsHorizontal内部で管理 */}
      <FieldsHorizontal />

      {/* Section 4: Concept */}
      <section id="concept" ref={conceptVideo.ref as React.RefObject<HTMLElement>} className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden">
        {/* 動画背景 — Lazy Load */}
        <div className="absolute inset-0 z-0">
          <video
            autoPlay
            loop
            muted
            playsInline
            className="absolute inset-0 w-full h-full object-cover opacity-75"
            style={{ transform: "translateZ(0)", willChange: "transform", backfaceVisibility: "hidden" }}
          >
            {conceptVideo.src && <source src={conceptVideo.src} type="video/mp4" />}
          </video>
          {/* B: 呼吸アニメーション */}
          <motion.div
            className="absolute inset-0 bg-gradient-to-b from-background/40 via-background/15 to-background/50"
            animate={{ opacity: [1.45, 0.65, 1.45] }}
            transition={{ duration: 8, repeat: Infinity, ease: "easeInOut", delay: 1 }}
          />
        </div>

        {/* コンセプト文 */}
        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ ...springPresets.gentle, duration: 1.2 }}
          >
            <p
            className="text-4xl md:text-6xl lg:text-7xl font-light tracking-widest mb-4"
              style={{
                fontFamily: "'Rajdhani', sans-serif",
                color: "oklch(0.88 0.18 195)",
                textShadow: "0 2px 12px rgba(0,0,0,0.9), 0 0 40px oklch(0.75 0.20 195 / 0.8), 0 0 80px oklch(0.65 0.18 195 / 0.5), 0 0 120px oklch(0.55 0.16 195 / 0.3)"
              }}
            >
              Where reality ends,
              <br />
              stories begin.
            </p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ ...springPresets.gentle, delay: 0.5, duration: 1.2 }}
          >
            <p
              className="text-xl md:text-2xl font-light tracking-wider"
              style={{
                fontFamily: "'Noto Sans JP', sans-serif",
                color: "rgba(255,255,255,0.90)",
                textShadow: "0 2px 10px rgba(0,0,0,0.95), 0 0 30px rgba(0,0,0,0.7)"
              }}
            >
              現実の果てに、物語は生まれる。
            </p>
          </motion.div>

          {/* スクロールダウン */}
          <motion.div
            className="mt-20"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 1.0 }}
          >
            <a href="#tech" className="inline-flex flex-col items-center gap-2 text-white/30 hover:text-white/60 transition-colors">
              <span className="text-xs tracking-widest uppercase">Next</span>
              <motion.div
                animate={{ y: [0, 6, 0] }}
                transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                </svg>
              </motion.div>
            </a>
          </motion.div>
        </div>
      </section>

      {/* Section 5: Tech */}
      <section id="tech" ref={techVideo.ref as React.RefObject<HTMLElement>} className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden">
        {/* 動画背景 — Lazy Load */}
        <div className="absolute inset-0 z-0">
          <video
            autoPlay
            loop
            muted
            playsInline
            className="absolute inset-0 w-full h-full object-cover opacity-75"
            style={{ transform: "translateZ(0)", willChange: "transform", backfaceVisibility: "hidden" }}
          >
            {techVideo.src && <source src={techVideo.src} type="video/mp4" />}
          </video>
          {/* B: 呼吸アニメーション */}
          <motion.div
            className="absolute inset-0 bg-gradient-to-b from-background/40 via-background/15 to-background/50"
            animate={{ opacity: [1.45, 0.65, 1.45] }}
            transition={{ duration: 9, repeat: Infinity, ease: "easeInOut", delay: 2 }}
          />
        </div>

        {/* コピー文 */}
        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ ...springPresets.gentle, duration: 1.2 }}
          >
            {/* 英語 1行目 — 大きく・青緑発光 */}
            <p
              className="text-3xl md:text-5xl lg:text-6xl font-light tracking-widest mb-2"
              style={{
                fontFamily: "'Rajdhani', sans-serif",
                color: "oklch(0.88 0.18 195)",
                textShadow: "0 2px 16px rgba(0,0,0,0.95), 0 0 40px oklch(0.75 0.20 195 / 0.8), 0 0 80px oklch(0.65 0.18 195 / 0.5), 0 0 120px oklch(0.55 0.16 195 / 0.3)"
              }}
            >
              Push beyond limits with cutting-edge AI.
            </p>
            {/* 日本語 1行目 — 小さく */}
            <p
              className="text-base md:text-lg lg:text-xl font-light tracking-wider mb-10"
              style={{
                fontFamily: "'Noto Sans JP', sans-serif",
                color: "rgba(255,255,255,0.65)",
                textShadow: "0 2px 10px rgba(0,0,0,0.95), 0 0 20px rgba(0,0,0,0.6)"
              }}
            >
              最先端のAIで、表現の限界を超える。
            </p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ ...springPresets.gentle, delay: 0.5, duration: 1.2 }}
          >
            {/* 英語 2行目 — 大きく・青緑発光 */}
            <p
              className="text-3xl md:text-5xl lg:text-6xl font-light tracking-widest mb-2"
              style={{
                fontFamily: "'Rajdhani', sans-serif",
                color: "oklch(0.88 0.18 195)",
                textShadow: "0 2px 16px rgba(0,0,0,0.95), 0 0 40px oklch(0.75 0.20 195 / 0.8), 0 0 80px oklch(0.65 0.18 195 / 0.5), 0 0 120px oklch(0.55 0.16 195 / 0.3)"
              }}
            >
              The future of filmmaking is already here.
            </p>
            {/* 日本語 2行目 — 小さく */}
            <p
              className="text-base md:text-lg lg:text-xl font-light tracking-wider"
              style={{
                fontFamily: "'Noto Sans JP', sans-serif",
                color: "rgba(255,255,255,0.65)",
                textShadow: "0 2px 10px rgba(0,0,0,0.95), 0 0 20px rgba(0,0,0,0.6)"
              }}
            >
              映像制作の未来は、もう始まっている。
            </p>
          </motion.div>

          {/* スクロールダウン */}
          <motion.div
            className="mt-20"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 1.0 }}
          >
            <a href="#links" className="inline-flex flex-col items-center gap-2 text-white/30 hover:text-white/60 transition-colors">
              <span className="text-xs tracking-widest uppercase">Links</span>
              <motion.div
                animate={{ y: [0, 6, 0] }}
                transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                </svg>
              </motion.div>
            </a>
          </motion.div>
        </div>
      </section>

      {/* Section 6: Links */}
      <section id="links" className="relative py-24 px-4 bg-background overflow-hidden">
        {/* Links背景スライドショー */}
        <LinksBgSlideshow />
        <div className="absolute inset-0 z-[1] bg-gradient-to-b from-background/50 via-background/20 to-background/60" />
        <motion.div
          className="relative z-10 container mx-auto max-w-5xl"
          variants={sectionContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
        >
          <motion.div variants={sectionItemUp} className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold mb-4">
              Links
            </h2>
            <p className="text-muted-foreground text-lg">
              各プラットフォームで作品を公開中
            </p>
          </motion.div>

          {/* Instagram - 大きく表示 */}
          <motion.div variants={sectionItemUp} className="mb-8">
            <a
              href="https://www.instagram.com/pictwork_studio"
              target="_blank"
              rel="noopener noreferrer"
              className="block group relative"
            >
              <div
                className="relative rounded-2xl p-8 transition-all duration-500 group-hover:-translate-y-1"
                style={{
                  background: "rgba(0,0,0,0.25)",
                  boxShadow:
                    "0 0 0 1px rgba(100,220,220,0.15)," +
                    "0 0 12px rgba(80,200,220,0.12)," +
                    "0 0 32px rgba(60,180,210,0.06)",
                  transition: "box-shadow 0.5s ease, transform 0.5s ease, background 0.5s ease",
                }}
                onMouseEnter={e => {
                  const el = e.currentTarget as HTMLElement;
                  el.style.boxShadow =
                    "0 0 0 1.5px rgba(140,240,255,0.45)," +
                    "0 0 20px rgba(80,200,230,0.35)," +
                    "0 0 60px rgba(60,180,210,0.18)," +
                    "0 0 100px rgba(40,160,200,0.08)";
                  el.style.background = "rgba(0,0,0,0.12)";
                }}
                onMouseLeave={e => {
                  const el = e.currentTarget as HTMLElement;
                  el.style.boxShadow =
                    "0 0 0 1px rgba(100,220,220,0.15)," +
                    "0 0 12px rgba(80,200,220,0.12)," +
                    "0 0 32px rgba(60,180,210,0.06)";
                  el.style.background = "rgba(0,0,0,0.25)";
                }}
              >
                <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 to-pink-500/10 opacity-0 group-hover:opacity-100 transition-all duration-500 rounded-2xl" />
                <div className="relative flex items-center gap-6">
                  <div className="p-4 rounded-xl group-hover:scale-110 transition-transform duration-300" style={{ background: "rgba(225,48,108,0.15)", color: "#e1306c" }}>
                    <Instagram className="w-10 h-10" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl md:text-3xl font-semibold mb-1 group-hover:text-primary transition-colors">
                      Instagram
                    </h3>
                    <p className="text-base md:text-lg text-foreground/90 leading-relaxed">
                      23K+ Followers — AI serialized story of beastfolk in another world
                    </p>
                    <p className="text-sm text-muted-foreground/60 mt-1">
                      23K+ フォロワー - 獣人たちの異世界日常を描くAI映像連載
                    </p>
                  </div>
                  <svg className="w-8 h-8 text-muted-foreground group-hover:text-primary group-hover:translate-x-2 transition-all" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            </a>
          </motion.div>

          {/* LinkedIn と X - 半分ずつ */}
          <div className="grid md:grid-cols-2 gap-6 mb-12">
            <motion.div variants={sectionItemLeft}>
              <a
                href="https://www.linkedin.com/in/tachiki-keinosuke-59080a309/"
                target="_blank"
                rel="noopener noreferrer"
                className="block group relative h-full"
              >
                <div
                  className="relative rounded-2xl p-6 h-full transition-all duration-500 group-hover:-translate-y-1"
                  style={{
                    background: "rgba(0,0,0,0.25)",
                    boxShadow:
                      "0 0 0 1px rgba(100,220,220,0.15)," +
                      "0 0 12px rgba(80,200,220,0.12)," +
                      "0 0 32px rgba(60,180,210,0.06)",
                    transition: "box-shadow 0.5s ease, transform 0.5s ease, background 0.5s ease",
                  }}
                  onMouseEnter={e => {
                    const el = e.currentTarget as HTMLElement;
                    el.style.boxShadow =
                      "0 0 0 1.5px rgba(140,240,255,0.45)," +
                      "0 0 20px rgba(80,200,230,0.35)," +
                      "0 0 60px rgba(60,180,210,0.18)," +
                      "0 0 100px rgba(40,160,200,0.08)";
                    el.style.background = "rgba(0,0,0,0.12)";
                  }}
                  onMouseLeave={e => {
                    const el = e.currentTarget as HTMLElement;
                    el.style.boxShadow =
                      "0 0 0 1px rgba(100,220,220,0.15)," +
                      "0 0 12px rgba(80,200,220,0.12)," +
                      "0 0 32px rgba(60,180,210,0.06)";
                    el.style.background = "rgba(0,0,0,0.25)";
                  }}
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-cyan-500/10 opacity-0 group-hover:opacity-100 transition-all duration-500 rounded-2xl" />
                  <div className="relative flex items-start gap-4">
                    <div className="p-3 rounded-xl group-hover:scale-110 transition-transform duration-300" style={{ background: "rgba(0,119,181,0.15)", color: "#0077b5" }}>
                      <SiLinkedin className="w-7 h-7" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl md:text-2xl font-semibold mb-1 group-hover:text-primary transition-colors">
                        LinkedIn
                      </h3>
                      <p className="text-sm text-foreground/90 leading-relaxed">
                        Professional Network
                      </p>
                      <p className="text-xs text-muted-foreground/60 mt-1">
                        プロフェッショナルネットワーク
                      </p>
                    </div>
                    <svg className="w-6 h-6 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              </a>
            </motion.div>

            <motion.div variants={sectionItemRight}>
              <a
                href="https://x.com/PictWorkStudio"
                target="_blank"
                rel="noopener noreferrer"
                className="block group relative h-full"
              >
                <div
                  className="relative rounded-2xl p-6 h-full transition-all duration-500 group-hover:-translate-y-1"
                  style={{
                    background: "rgba(0,0,0,0.25)",
                    boxShadow:
                      "0 0 0 1px rgba(100,220,220,0.15)," +
                      "0 0 12px rgba(80,200,220,0.12)," +
                      "0 0 32px rgba(60,180,210,0.06)",
                    transition: "box-shadow 0.5s ease, transform 0.5s ease, background 0.5s ease",
                  }}
                  onMouseEnter={e => {
                    const el = e.currentTarget as HTMLElement;
                    el.style.boxShadow =
                      "0 0 0 1.5px rgba(140,240,255,0.45)," +
                      "0 0 20px rgba(80,200,230,0.35)," +
                      "0 0 60px rgba(60,180,210,0.18)," +
                      "0 0 100px rgba(40,160,200,0.08)";
                    el.style.background = "rgba(0,0,0,0.12)";
                  }}
                  onMouseLeave={e => {
                    const el = e.currentTarget as HTMLElement;
                    el.style.boxShadow =
                      "0 0 0 1px rgba(100,220,220,0.15)," +
                      "0 0 12px rgba(80,200,220,0.12)," +
                      "0 0 32px rgba(60,180,210,0.06)";
                    el.style.background = "rgba(0,0,0,0.25)";
                  }}
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-cyan-500/10 opacity-0 group-hover:opacity-100 transition-all duration-500 rounded-2xl" />
                  <div className="relative flex items-start gap-4">
                    <div className="p-3 rounded-xl group-hover:scale-110 transition-transform duration-300" style={{ background: "rgba(255,255,255,0.1)", color: "#ffffff" }}>
                      <SiX className="w-7 h-7" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl md:text-2xl font-semibold mb-1 group-hover:text-primary transition-colors">
                        X (Twitter)
                      </h3>
                      <p className="text-sm text-foreground/90 leading-relaxed">
                        Behind the scenes &amp; latest updates
                      </p>
                      <p className="text-xs text-muted-foreground/60 mt-1">
                        制作の舞台裏・最新情報
                      </p>
                    </div>
                    <svg className="w-6 h-6 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              </a>
            </motion.div>
          </div>

          {/* 魔都市役所：特集ページリンク（削除する場合はこのブロックごと消す） */}
          {/* === EASTER EGG BLOCK START === */}
          <motion.div variants={sectionItemUp} className="flex justify-center mt-10">
            <a
              href="#/mato-city-gov"
              className="group flex items-center gap-3 px-5 py-3 rounded-xl border border-red-500/30 bg-black/30 hover:bg-red-900/20 hover:border-red-400/60 transition-all duration-500"
              style={{ boxShadow: "0 0 12px rgba(220,50,50,0.08)" }}
            >
              <span className="text-red-400/80 text-base">⚠</span>
              <div className="text-left">
                <div className="text-[11px] font-bold tracking-widest text-red-400/80 uppercase group-hover:text-red-300 transition-colors">
                  Demon City Gov — Pest Warning Site
                </div>
                <div className="text-[10px] text-muted-foreground/50 group-hover:text-muted-foreground/80 transition-colors">
                  魔都市政府 公式害獣警戒サイト
                </div>
              </div>
              <span className="text-muted-foreground/30 group-hover:text-red-300/60 transition-colors text-xs">→</span>
            </a>
          </motion.div>
          {/* === EASTER EGG BLOCK END === */}

          {/* イースターエッグ②：BBSへの極秘リンク */}
          <motion.div variants={sectionItemUp} className="flex justify-center mt-3">
            <a
              href="#/mato-bbs"
              className="group inline-flex items-center gap-2 transition-all duration-700"
              style={{ opacity: 0.0 }}
              onMouseEnter={e => ((e.currentTarget as HTMLAnchorElement).style.opacity = "1")}
              onMouseLeave={e => ((e.currentTarget as HTMLAnchorElement).style.opacity = "0.0")}
            >
              <span
                className="font-mono text-[9px] tracking-[0.4em] text-muted-foreground/30 group-hover:text-indigo-300/80 transition-colors duration-700"
                style={{ letterSpacing: "0.5em" }}
              >
                ∿ ∿ ∿
              </span>
              <span className="font-mono text-[8px] tracking-widest text-muted-foreground/20 group-hover:text-indigo-200/60 transition-colors duration-700">
                声が、聞こえる
              </span>
              <span
                className="font-mono text-[9px] tracking-[0.4em] text-muted-foreground/30 group-hover:text-indigo-300/80 transition-colors duration-700"
                style={{ letterSpacing: "0.5em" }}
              >
                ∿ ∿ ∿
              </span>
            </a>
          </motion.div>

          {/* Social Icons */}
          <motion.div variants={sectionItemUp} className="flex justify-center mt-6">
            <SocialLinks />
          </motion.div>
        </motion.div>
      </section>

      {/* Section 7: Contact */}
      <section id="contact" ref={contactVideo.ref as React.RefObject<HTMLElement>} className="relative py-24 px-4 bg-background overflow-hidden">
        {/* 動画背景 — Lazy Load */}
        <div className="absolute inset-0 z-0">
          <video
            autoPlay
            loop
            muted
            playsInline
            className="absolute inset-0 w-full h-full object-cover opacity-65"
            style={{ transform: "translateZ(0)", willChange: "transform", backfaceVisibility: "hidden" }}
          >
            {contactVideo.src && <source src={contactVideo.src} type="video/mp4" />}
          </video>
          {/* B: 呼吸アニメーション */}
          <motion.div
            className="absolute inset-0 bg-gradient-to-b from-background/60 via-background/25 to-background/70"
            animate={{ opacity: [1.4, 0.68, 1.4] }}
            transition={{ duration: 8, repeat: Infinity, ease: "easeInOut", delay: 3 }}
          />
        </div>
        <motion.div
          className="relative z-10 container mx-auto max-w-3xl"
          variants={sectionContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
        >
          <motion.div variants={sectionItemScale} className="relative">
            <div className="relative bg-black/30 border border-white/10 rounded-2xl p-8 md:p-12 transition-all duration-500 hover:bg-black/20">
              <ContactForm />
            </div>
          </motion.div>

          {/* Footer */}
          <motion.div variants={sectionItemUp} className="mt-16 text-center">
            <p className="text-sm text-muted-foreground mb-2">
              © 2026 Keinosuke Tachiki / PictWork STUDIO
            </p>
            <p className="text-xs text-muted-foreground">
              All rights reserved.
            </p>
            {/* イースターエッグ：秘密のドアへのリンク（コアファン向け） */}
            <a
              href="#/secret-door"
              className="group inline-flex flex-col items-center gap-1 mt-6 transition-all duration-700"
              title=""
            >
              {/* ドアのミニアイコン（薄く浮かんでいる） */}
              <svg viewBox="0 0 24 36" width="14" height="21"
                className="opacity-[0.08] group-hover:opacity-[0.45] transition-opacity duration-700"
                style={{ filter: "drop-shadow(0 0 4px rgba(130,70,220,0))" }}
              >
                <rect x="1" y="1" width="22" height="34" rx="2" fill="none" stroke="currentColor" strokeWidth="1.5"/>
                <circle cx="18" cy="18" r="2" fill="currentColor" opacity="0.6"/>
              </svg>
              {/* テキスト：ほんのり見える */}
              <span className="font-mono text-[9px] tracking-[0.5em] text-muted-foreground/[0.08] group-hover:text-muted-foreground/40 transition-colors duration-700">
                · · ·
              </span>
            </a>
          </motion.div>
        </motion.div>
      </section>
    </div>
  );
}